import React, { useRef, useState } from 'react';
import {
    makeStyles,
    Grid,
    Typography,
    IconButton,
    Tooltip,
    Box,
} from '@material-ui/core';
import SignatureCanvas from 'react-signature-canvas';
import { Edit as EditIcon, RefreshCw as ResetIcon } from 'react-feather';
import { ISignatures } from './types';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
    signContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(1),
        position: 'relative',
    },
    canvasWrapper: {
        position: 'relative',
    },
    editButton: {
        position: 'absolute',
        top: 10,
        right: 10,
    },
    resetButton: {
        position: 'absolute',
        top: 10,
        right: 10,
    },
    image: {
        width: CANVAS_SIZE.width,
        height: CANVAS_SIZE.height,
        backgroundColor: '#f5f5f5',
        position: 'relative',
    },
}));

const CANVAS_SIZE = {
    width: 250,
    height: 100,
};

export const Signatures = ({
    signs,
    setFieldValue,
    isEditable,
    ...props
}: ISignatures) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const signatureRefs = useRef<(SignatureCanvas | null)[]>([]);
    const [isEditing, setIsEditing] = useState<boolean[]>([]);

    const handleSign = (index: number) => {
        const signatureRef = signatureRefs.current[index];
        if (signatureRef) {
            const signatureDataURL = signatureRef
                .getTrimmedCanvas()
                .toDataURL('image/png');
            setFieldValue(`signs[${index}].sign`, signatureDataURL);
        }
    };

    const toggleEditing = (index: number, editing: boolean) => {
        setIsEditing((prev) => {
            const newEditing = [...prev];
            newEditing[index] = editing;
            return newEditing;
        });
    };

    const handleReset = (index: number) => {
        const signatureRef = signatureRefs.current[index];
        if (signatureRef) {
            signatureRef.clear();
            setFieldValue(`signs[${index}].sign`, '');
            toggleEditing(index, true);
        }
    };

    return (
        <Grid container spacing={2} {...props}>
            {signs?.map((sign, index) => (
                <Grid
                    key={index}
                    item
                    xs={12}
                    sm={signs.length > 1 ? 6 : 12}
                    className={classes.signContainer}
                >
                    {isEditing[index] ? (
                        <Box className={classes.canvasWrapper}>
                            <SignatureCanvas
                                canvasProps={{
                                    width: CANVAS_SIZE.width,
                                    height: CANVAS_SIZE.height,
                                }}
                                backgroundColor={'#f5f5f5'}
                                onEnd={() => handleSign(index)}
                                ref={(el) =>
                                    (signatureRefs.current[index] = el)
                                }
                            />
                            <Tooltip title={t('evaluation.reset')}>
                                <IconButton
                                    className={classes.resetButton}
                                    onClick={() => handleReset(index)}
                                    size="small"
                                >
                                    <ResetIcon size={16} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ) : (
                        <Box className={classes.image}>
                            <img src={sign.sign} />
                            {isEditable && (
                                <Tooltip title={t('evaluation.editSignature')}>
                                    <IconButton
                                        className={classes.editButton}
                                        onClick={() =>
                                            toggleEditing(index, true)
                                        }
                                        size="small"
                                    >
                                        <EditIcon size={16} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    )}

                    <Typography variant="h5" color="textPrimary">
                        {sign?.description}
                    </Typography>
                </Grid>
            ))}
        </Grid>
    );
};
